/**
 * "Marca no vídeo" (``channel_settings.video_brand_mode``, migration 0084):
 * what the channel's videos carry in the watermark, the cover and the APOD end
 * card. The same video goes to more than one platform and the @ differs
 * between them (YouTube's often has a suffix, like ``@EsquerdaEmPautaYT``), so
 * a channel can swap the @ for its name, or for nothing. The description keeps
 * the @ in every mode.
 *
 * Pure logic, kept out of the component so it is testable without a DOM.
 */
import type { ChannelSettings } from '@/api/endpoints/channels'

export type VideoBrandMode = ChannelSettings['video_brand_mode']

export const DEFAULT_VIDEO_BRAND_MODE: VideoBrandMode = 'handle'

/** Mirrors ``NAME_BRAND_MAX_CHARS`` in ``src/services/video/brand.py``. */
export const NAME_BRAND_MAX_CHARS = 40

export const VIDEO_BRAND_OPTIONS: ReadonlyArray<{ value: VideoBrandMode; label: string }> = [
  { value: 'handle', label: '@ do canal' },
  { value: 'name', label: 'Nome do canal' },
  { value: 'none', label: 'Nada' },
]

/**
 * Settings as a backend sends them. The front goes out on the merge
 * (Cloudflare Pages) and the backend on the skab's pull, so for a while the
 * answer comes from a backend older than migration 0084, without the field.
 */
type BrandSettings = Partial<Pick<ChannelSettings, 'video_brand_mode'>> | null | undefined

/** The saved mode, or the default when the backend doesn't send one. */
export function savedVideoBrandMode(settings: BrandSettings): VideoBrandMode {
  return settings?.video_brand_mode ?? DEFAULT_VIDEO_BRAND_MODE
}

/**
 * Whether the backend knows the setting. When it doesn't, the section hides:
 * a save would be dropped and still answered with 200.
 */
export function backendKnowsVideoBrand(settings: BrandSettings): boolean {
  return settings?.video_brand_mode !== undefined
}

/** Whether a save came back with the mode it sent — the only honest "saved". */
export function savedAsSent(response: BrandSettings, sent: VideoBrandMode): boolean {
  return response?.video_brand_mode === sent
}

export interface VideoBrandChannel {
  name: string
  /** The @ as the settings resolve it (empty when the channel has none). */
  channel_tag: string
}

/** The name as the watermark draws it: cut with an ellipsis past the limit, like the backend. */
function fitName(name: string): string {
  const chars = Array.from(name) // code points, as Python counts them
  if (chars.length <= NAME_BRAND_MAX_CHARS) return name
  return chars.slice(0, NAME_BRAND_MAX_CHARS - 1).join('').trimEnd() + '…'
}

/** What a new video will carry under ``mode`` — ``''`` when nothing. */
export function videoBrandText(mode: VideoBrandMode, channel: VideoBrandChannel): string {
  if (mode === 'none') return ''
  if (mode === 'name') return fitName(channel.name.trim())
  return channel.channel_tag.trim()
}

/** The help line under the selector: what the choice does to new videos. */
export function videoBrandHelp(mode: VideoBrandMode, channel: VideoBrandChannel): string {
  const text = videoBrandText(mode, channel)
  if (mode === 'none') {
    return 'Os vídeos novos saem sem marca do canal. A descrição continua com o @.'
  }
  if (mode === 'name') {
    return text
      ? `Os vídeos novos mostram “${text}”, sem @. Serve em qualquer plataforma, mesmo com @ diferentes.`
      : 'O canal está sem nome, então os vídeos novos sairiam sem marca.'
  }
  return text
    ? `Os vídeos novos mostram ${text}. Use quando o @ for o mesmo em todas as plataformas.`
    : 'O canal está sem @, então os vídeos novos saem sem marca.'
}
